import {
    checkModalValidationsForAutoloaderTestPackage,
    checkModalValidationsForConfig,
    checkModalValidationsForTestPackage, checkTesseractAddTestPackageModalValidations,
    checkZancaSortAddTestPackageModalValidations, hasUnavailableFloor,
    validateZancaSortLocationConfig,
    validateZancaSortTierConfig, checkTierConfigLevelOverlap,
    parseLocationType, buildLocationType,
    validateVaprLocationConfig,
    validateLocationMappingRow,
    areStackedStowTestFieldsWellFormed,
    findConflictingBagTier
} from "./Validations";
import {
    ADTA_ASSET_TYPE,
    ADTF_ASSET_TYPE, AUTO_LOADER_ASSET_TYPE,
    COMMA,
    EMPTY_STRING,
    SEMI_COLON,
    INSTALLATION_TYPES,
    TESSERACT_ASSET_TYPE,
    ZANCA_SORT_TIER_NAMES
} from "./Constants";
import {getLocalizedMessage} from "./Localization";
import {getVaprLocationTypeLabel, getVaprLocationTypeValue, getVaprOrientationValue} from "./Util";
import {sprintf} from 'sprintf-js';
import {
    getInstallationTypeValues,
    getFloorsForSlide,
    getFloorsWithMultipleAssignedSlides,
    getSorterConfigForInstallationType
} from "./Util";
import {FEATURES, FeatureUtility, isNewZancaSortTestConfigEnabled} from "./FeatureUtility";

const commonCSVValidation = (str, parser) => {
    if (str.indexOf("\n") === str.length) {
        return {error: "CSV file has no rows"}
    }
    const head = str.slice(0, str.indexOf("\n"))
    const header = head.replace(/\s/g,'').toLowerCase().split(',');
    const headerCheck = parser.FIELDS.reduce((result, val, index) => {
        return result && val.toLowerCase() === header[index]
    }, true);
    if (header.length !== parser.FIELDS.length || !headerCheck) {
        return {error: "CSV file headers are incorrect. Expected headers: " + JSON.stringify(parser.FIELDS), }
    }
    return str.slice(str.indexOf("\n") + 1).split("\n");
}

export const escapeDoubleQuotes = (data) => {
    return '"' + data.replace(/"/g, '\'') + '"';
}

export const SorterConfigCSVParser = {
    FIELDS: ["DestinationId", "InstallationType", "Locations"],
    SAMPLE: 'DestinationId,InstallationType,Locations\n4001,aisle,"A,1;B,1"\n4002,jackpot,Finger A',
    STRINGIFY: (assets) => {
        let res = "DestinationId,InstallationType,Locations\n";
        assets.forEach((asset) => {
            if(['jackpot', 'miss', 'recirculation','problem_solve'].includes(asset.installation.type)) {
                res += `${asset.bayId},${asset.installation.type},${asset.fingerName}\n`
            } else {
                let locationString = "";
                asset.installation.locations.forEach((location, index) => {
                    if(index) {
                        locationString += `;${location}`;
                    } else {
                        locationString += location;
                    }
                })
                res += `${asset.bayId},${asset.installation.type},"${locationString}"\n`
            }
        })
        return res;
    },
    PARSE: (str, mac) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, SorterConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        // Process each row
        const sorterConfig = [];
        const invalidInstallationTypeIndex = [];
        let hasJackpot = false;
        const validInstallationType = getInstallationTypeValues();
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate sorter config from values
            const values = row.split(',');
            const config = {
                bayId: values[0].replace(/\s/g,''),
                installation: {
                    type: values[1].toLowerCase().replace(/\s/g,'')
                }
            }

            // add index of row to list if installationType is invalid
            !validInstallationType.includes(config.installation.type) && invalidInstallationTypeIndex.push(index+1);
            // set has Jackpot to true if config contains a Jackpot value
            if (INSTALLATION_TYPES.JACKPOT.value === config.installation.type) {
                hasJackpot = true;
            }
            if (['miss','jackpot','recirculation','problem_solve'].includes(config.installation.type)) {
                config.fingerName = values[2].trim()
            } else {
                config.installation.locations = values.slice(2).join(',').toUpperCase().replace(/\s|"/g,'').split(';');
            }
            // Validate row
            const validateConfig = JSON.parse(JSON.stringify(config))
            if (validateConfig.installation.locations) {
                validateConfig.installation.locations = validateConfig.installation.locations.join(";")
            }
            if(!checkModalValidationsForConfig(validateConfig, mac, ADTA_ASSET_TYPE)) {
                console.log("ERROR = ", validateConfig);
                console.log("MAC = ", mac);
                return {error: "Values of row number " + (index+1) + " are invalid"}
            }
            // Add to list
            sorterConfig.push(config);
        }
        if (!hasJackpot) {
            return {error : "Config is missing jackpot destination"}
        }
        if (FeatureUtility.getFeatureStatus(FEATURES.IS_MULTIPLE_BULKY_SLIDES_FOR_DROP_TO_CART_ENABLED)) {
            const bulkyValidationError = validateMultiSlideAssignmentsForInstallation(sorterConfig, INSTALLATION_TYPES.BULKY);
            if (bulkyValidationError) return bulkyValidationError;
        }
        if (FeatureUtility.getFeatureStatus(FEATURES.IS_MULTIPLE_FRAGILE_SLIDES_ENABLED_FOR_SSD) ) {
            const fragileValidationError = validateMultiSlideAssignmentsForInstallation(sorterConfig, INSTALLATION_TYPES.FRAGILE);
            if (fragileValidationError) return fragileValidationError;
        }
        return invalidInstallationTypeIndex.length > 0
            ? {error: "Installation type is invalid for " + invalidInstallationTypeIndex.length + " row(s): "
                    + invalidInstallationTypeIndex.slice(0, Math.min(20, invalidInstallationTypeIndex.length))
        }
            : sorterConfig;
    }
}

/**
 * Validates multi slide assignments for installation type
 * @param sorterConfig
 * @param installationType
 * @returns {{error: string}|null}
 */
const validateMultiSlideAssignmentsForInstallation = (sorterConfig, installationType) => {

    const floorSorterConfig = getSorterConfigForInstallationType(sorterConfig, INSTALLATION_TYPES.FLOOR);
    const slideSorterConfig = getSorterConfigForInstallationType(sorterConfig, installationType);
    const floorsForSlide = getFloorsForSlide(slideSorterConfig);

    if (!hasUnavailableFloor(floorSorterConfig, floorsForSlide)) {
        return {error: `Floors assigned to ${installationType.value} slides are invalid`};
    }

    const floorsWithMultipleAssignedSlides = getFloorsWithMultipleAssignedSlides(floorSorterConfig, floorsForSlide);
    if (floorsWithMultipleAssignedSlides.length > 0) {
        const locationList = floorsWithMultipleAssignedSlides.length > 5
            ? `${floorsWithMultipleAssignedSlides.slice(0, 5)} and ${floorsWithMultipleAssignedSlides.length - 5} more`
            : floorsWithMultipleAssignedSlides;
        return {error: `Locations: ${locationList} are having more than 1 ${installationType.value} slide assigned and are invalid`};
    }

    return null;
};

export const ADTFSorterConfigCSVParser = {
    FIELDS: ["DestinationId", "SorterId", "InstallationType", "Locations"],
    SAMPLE: 'DestinationId,SorterId,InstallationType,Locations\nL01#0,1,cluster,"A"\nJP#0,1,miss,""',
    STRINGIFY: (assets) => {
        let res = "DestinationId,SorterId,InstallationType,Locations\n";
        assets.forEach((asset) => {
            if(['miss', 'jackpot', 'ov', INSTALLATION_TYPES.FRAGILE.value].includes(asset.installation.type)) {
                res += `${asset.destinationId},${asset.sorterId},${asset.installation.type},""\n`
            } else {
                let locationString = "";
                asset.installation.locations.forEach((location, index) => {
                    if(index) {
                        locationString += `;${location}`;
                    } else {
                        locationString += location;
                    }
                })
                res += `${asset.destinationId},${asset.sorterId},${asset.installation.type},"${locationString}"\n`
            }
        })
        return res;
    },
    PARSE: (str, mac) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, ADTFSorterConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        // Process each row
        const sorterConfig = [];
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate sorter config from values
            const values = row.split(',');
            const config = {
                destinationId: values[0].replace(/\s/g,''),
                sorterId: values[1].replace(/\s/g,''),
                installation: {
                    type: values[2].toLowerCase().replace(/\s/g,'')
                }
            }
            if (['miss', 'jackpot', 'ov', INSTALLATION_TYPES.FRAGILE.value].includes(config.installation.type)) {
                config.installation.locations = [];
            } else {
                config.installation.locations = values.slice(3).join(',').toUpperCase().replace(/\s|"/g,'').split(';');
            }
            // Validate row
            const validateConfig = JSON.parse(JSON.stringify(config))
            if (validateConfig.installation.locations) {
                validateConfig.installation.locations = validateConfig.installation.locations.join(";")
            }
            // Validate row
            if(!checkModalValidationsForConfig(validateConfig, mac, ADTF_ASSET_TYPE)) {
                return {error: "Values of row number " + (index+1) + " are invalid"}
            }
            // Add to list
            sorterConfig.push(config);
        }
        return sorterConfig;
    }
}

export const TestConfigCSVParser = {
    FIELDS: ["Scannables", "SortZone"],
    SAMPLE: 'Scannables,SortZone\nTBA123456789,A1-1A\nTBA123456790,B1-1B',
    STRINGIFY: (assets) => {
        let res = "Scannables,SortZone\n";
        assets.forEach((asset) => {
            let scannablesString = "";
            asset.scannables.forEach((scannable, index) => {
                if(index) {
                    scannablesString += `;${scannable}`
                } else {
                    scannablesString += scannable;
                }
            });
            res += `${scannablesString},${asset.sortZone}\n`
        })
        return res;
    },
    PARSE: (str) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, TestConfigCSVParser);

        // Process rows
        const sorterConfig = [];
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate sorter config from values
            const values = row.replace(/\s/g,'').split(',');
            const config = {
                scannables: values[0].split(';'),
                sortZone: values[1]
            }
            // Validate row
            if(!checkModalValidationsForTestPackage(config)) {
                return {error: "Values of row number " + (index+1) + " are invalid"}
            }
            // Add to list
            sorterConfig.push(config);
        }
        return sorterConfig;
    }
}

/**
 * Test package parser for an ADTF that takes part in stacked stow, carrying the two extra stacked stow columns.
 */
export const StackedStowTestConfigCSVParser = {
    FIELDS: ["Scannables", "SortZone", "Bag", "Tier"],
    SAMPLE: 'Scannables,SortZone,Bag,Tier\nTBA123456789,A1-1A,BAG-TEST-001,2\nTBA123456790,A1-1B,BAG-TEST-002,1',
    STRINGIFY: (assets) => {
        let res = "Scannables,SortZone,Bag,Tier\n";
        assets.forEach((asset) => {
            const scannablesString = asset.scannables.join(SEMI_COLON);
            res += `${scannablesString},${asset.sortZone},${asset.bag || EMPTY_STRING},`
                + `${asset.tier || EMPTY_STRING}\n`;
        })
        return res;
    },
    PARSE: (str) => {
        const rows = commonCSVValidation(str, StackedStowTestConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        const sorterTestPackages = [];
        for (let index = 0; index < rows.length; index++) {
            const row = rows[index];
            if (!row) {
                continue;
            }
            const values = row.replace(/\s/g, '').split(COMMA);
            const config = {
                scannables: values[0].split(SEMI_COLON),
                sortZone: values[1]
            }
            // Omitted so that an untouched row stays identical to its persisted form
            if (values[2]) config.bag = values[2];
            if (values[3]) config.tier = values[3];

            if (!checkModalValidationsForTestPackage(config) || !areStackedStowTestFieldsWellFormed(config)) {
                return {error: "Values of row number " + (index + 1) + " are invalid"}
            }
            const conflict = findConflictingBagTier(sorterTestPackages, config);
            if (conflict) {
                return {
                    error: "Row number " + (index + 1) + " sets tier " + config.tier + " for bag "
                        + config.bag + ", which another row already assigns to tier " + conflict.tier
                }
            }
            sorterTestPackages.push(config);
        }
        return sorterTestPackages;
    }
}

// Same CSV layout as TestConfigCSVParser. A package id can belong to one row only, while a sort zone can
// appear on any number of rows.
export const AslInductTestPackageCSVParser = {
    FIELDS: ["Scannables", "SortZone"],
    SAMPLE: 'Scannables,SortZone\nTBA123456789;TBA123456790,A1-1A\nTBA123456791,A-1.1A',
    STRINGIFY: TestConfigCSVParser.STRINGIFY,
    PARSE: (str) => {
        const rows = commonCSVValidation(str, AslInductTestPackageCSVParser);
        if (rows.error) {
            return rows;
        }

        const testPackages = [];
        const scannableToRow = new Map();
        for (let index = 0; index < rows.length; index++) {
            // Trimmed so the "\r" left by a blank line in a Windows-saved CSV counts as empty too.
            const row = rows[index].trim();
            if (!row) {
                continue;
            }
            const values = row.replace(/\s/g,'').split(',');
            const config = {
                scannables: values[0].split(';').filter(Boolean),
                sortZone: values[1]
            }
            // filter(Boolean) turns an empty cell into [], which the shared check would accept.
            if (!config.scannables.length || !checkModalValidationsForTestPackage(config)) {
                return {error: "Values of row number " + (index + 1) + " are invalid"}
            }
            // A Map rather than findScannableConflict: uploads can run to thousands of rows.
            for (const scannable of config.scannables) {
                const firstRow = scannableToRow.get(scannable);
                if (firstRow && firstRow.number === index + 1) {
                    return {error: `Package ${scannable} is listed twice in row ${index + 1}`};
                }
                if (firstRow) {
                    return {error: `Package ${scannable} in row ${index + 1} is already in row ${firstRow.number}`
                            + ` (sort zone ${firstRow.sortZone})`};
                }
                scannableToRow.set(scannable, {number: index + 1, sortZone: config.sortZone});
            }
            testPackages.push(config);
        }
        return testPackages;
    }
}

export const ARTestConfigCSVParser = {
    FIELDS: ["Picklist", "PickItem", "ItemType", "Cluster", "PickPoint"],
    STRINGIFY: (data) => {
        let res = "Picklist,PickItem,ItemType,Cluster,PickPoint\n";
        let index = 1;
        data.forEach((picklist) => {
            picklist.pickItems.forEach((item) => {
                res += sprintf('P%d,%s,%s,%s,%s\n', index, item.id, item.type, item.cluster, picklist.retrievalLocation);
            })
            index++;
        })
        return res;
    }
}

// FIELDS and SAMPLE are getters because feature flags load from SCC after this module is imported.
export const ZancaSortTestConfigCSVParser = {
    get FIELDS() {
        return isNewZancaSortTestConfigEnabled() ?
            ["Scannables", "SortZone", "Bags"] :
            ["Scannables", "Destination"];
    },
    get SAMPLE() {
        return isNewZancaSortTestConfigEnabled() ?
            'Scannables,SortZone,Bags\nTBA123456789,A1-1A,BAG-TEST-01-ZS;BAG-TEST-02-ZS\nTBA123456790,B1-1B,BAG-TEST-03-ZS' :
            'Scannables,Destination\nTBA123456789,BAG-TEST-01-ZS\nTBA123456790,BAG-TEST-02-ZS';
    },
    STRINGIFY: (assets) => {
        let res =  isNewZancaSortTestConfigEnabled() ?
            "Scannables,SortZone,Bags\n" :
            "Scannables,Destination\n";

        assets.forEach((asset) => {
            let scannablesString = "";
            asset.scannables.forEach((scannable, index) => {
                if(index) {
                    scannablesString += `;${scannable}`
                } else {
                    scannablesString += scannable;
                }
            });

            if ( isNewZancaSortTestConfigEnabled()) {
                let containerListString = "";
                if (Array.isArray(asset.containerList) && asset.containerList.length > 0) {
                    asset.containerList.forEach((container, index) => {
                        if (index) {
                            containerListString += `;${container}`;
                        } else {
                            containerListString += container;
                        }
                    });
                } else {
                    containerListString = "";
                }
                res += `${scannablesString},${asset.destination},${containerListString}\n`
            } else {
                res += `${scannablesString},${asset.destination}\n`
            }
        })
        return res;
    },
    PARSE: (str) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, ZancaSortTestConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        // Process rows
        const testConfig = [];
        if ( isNewZancaSortTestConfigEnabled()) {
            const seenScannables = new Set();
            const containerToDestinationMap = new Map();

            for (let index = 0; index < rows.length; index++) {
                // Handle empty rows
                const row = rows[index];
                if (!row) {
                    continue;
                }
                // Generate test config from values
                const values = row.replace(/\s/g,'').split(',');
                const scannables = values[0].split(';');

                // Check for duplicate scannables
                const duplicates = scannables.filter(scannable => seenScannables.has(scannable));
                if (duplicates.length > 0) {
                    return {error: `Duplicate scannables found in row ${index + 1}: ${duplicates.join(", ")}`};
                }

                // Add scannables to seen set
                scannables.forEach(scannable => seenScannables.add(scannable));

                const destination = values[1];
                const containers = (values[2] || '').split(';').filter(Boolean);

                // Check for container to destination conflicts
                const conflictingContainers = containers.filter(container => {
                    const existingDestination = containerToDestinationMap.get(container);
                    return existingDestination && existingDestination !== destination;
                });

                if (conflictingContainers.length > 0) {
                    const conflicts = conflictingContainers.map(container =>
                        `${container} (already mapped to ${containerToDestinationMap.get(container)})`
                    ).join(', ');
                    return {
                        error: `Invalid container mapping in row ${index + 1}: ${conflicts}`
                    };
                }

                // Add new container mappings
                containers.forEach(container => {
                    containerToDestinationMap.set(container, destination);
                });

                const config = {
                    scannables: scannables,
                    destination: destination,
                    containerList: containers
                };

                // Validate row
                if(!checkZancaSortAddTestPackageModalValidations(config)) {
                    return {error: "Values of row number " + (index + 1) + " are invalid"};
                }

                testConfig.push(config);
            }
        } else {
            // Original code for old configuration
            for (let index = 0; index < rows.length; index++) {
                // Handle empty rows
                const row = rows[index];
                if (!row) {
                    continue;
                }
                // Generate test config from values
                const values = row.replace(/\s/g,'').split(',');
                const config = {
                    scannables: values[0].split(';'),
                    destination: values[1]
                }
                // Validate row
                if(!checkZancaSortAddTestPackageModalValidations(config)) {
                    return {error: "Values of row number " + (index+1) + " are invalid"}
                }
                // Add to list
                testConfig.push(config);
            }
        }
        return testConfig;
    }
}

export const ZancaSortLocationConfigCSVParser = {
    FIELDS: ["LocationId", "LocationType", "SubType"],
    SAMPLE: 'LocationId,LocationType,SubType\nWH-001,WAREHOUSE_LOCATION,\nSTS-001,TETRIS_STATION,A\nSTS-002,TETRIS_STATION,B',
    STRINGIFY: (assets) => {
        let res = "LocationId,LocationType,SubType\n";
        assets.forEach((asset) => {
            const {type, subType} = parseLocationType(asset.locationType);
            res += `${asset.locationId},${type},${subType}\n`
        })
        return res;
    },
    PARSE: (str) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, ZancaSortLocationConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        // Process rows, collecting the 1-based index of every invalid row so the
        // whole file can be rejected with a complete list rather than the first failure.
        const locationConfig = [];
        const invalidRows = [];
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate location config from values, combining type and sub type
            const values = row.replace(/\s/g,'').split(',');
            const config = {
                locationId: values[0],
                locationType: buildLocationType(values[1], values[2] || "")
            }
            // Validate row
            if (!validateZancaSortLocationConfig(config)) {
                invalidRows.push(index + 1);
                continue;
            }
            // Add to list
            locationConfig.push(config);
        }
        // Reject the whole file if any row failed validation
        if (invalidRows.length > 0) {
            return {error: "Error, file is not valid. Invalid rows: " + invalidRows.join(", ")}
        }
        return locationConfig;
    }
}

export const VaprLocationConfigCSVParser = {
    FIELDS: ["Location", "LocationType", "Orientation"],
    SAMPLE: 'Location,LocationType,Orientation\nSTS-01,ManualStation,UP\nSTS-02,TetrisStation,DOWN\nPKP-01,PickPoint,LEFT',
    STRINGIFY: (assets) => {
        let res = "Location,LocationType,Orientation\n";
        assets.forEach((asset) => {
            // LocationType is written as the UI label; the internal value ("STOW_STATION#MANUAL")
            // is never user facing. Orientation is already readable as stored, so it goes as-is.
            const locationType = getVaprLocationTypeLabel(asset.locationType);
            // Legacy entries may lack orientation; emit an empty cell rather than "undefined".
            const orientation = asset.orientation == null ? EMPTY_STRING : asset.orientation;
            res += `${asset.label},${locationType},${orientation}\n`
        })
        return res;
    },
    PARSE: (str) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, VaprLocationConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        const locationConfig = [];
        const invalidRows = [];
        const duplicateRows = [];
        const seenLabels = new Set();
        for (let index = 0; index < rows.length; index++) {
            const row = rows[index];
            if (!row) {
                continue;
            }
            const values = row.replace(/\s/g, '').split(',');
            // The file carries UI labels, so translate to the internal values before validating.
            // An unrecognised label maps to undefined and the row is reported as invalid below.
            const config = {
                label: values[0],
                locationType: getVaprLocationTypeValue(values[1])
            }
            if (values[2]) {
                // Fall back to the raw cell when it is not a known label, so validation rejects the
                // row. Mapping it to undefined instead would look like "orientation omitted", which
                // is allowed for legacy entries, and a typo would be silently accepted.
                config.orientation = getVaprOrientationValue(values[2]) || values[2];
            }
            if (!validateVaprLocationConfig(config, true)) {
                invalidRows.push(index + 1);
                continue;
            }
            // Location is the unique key for an entry (draft edit/delete and the table row key
            // all compare on it), so a file repeating one is rejected rather than staged twice.
            if (seenLabels.has(config.label)) {
                duplicateRows.push(index + 1);
                continue;
            }
            seenLabels.add(config.label);
            locationConfig.push(config);
        }
        if (invalidRows.length > 0) {
            return {error: "Error, file is not valid. Invalid rows: " + invalidRows.join(", ")}
        }
        if (duplicateRows.length > 0) {
            return {error: "Error, file contains duplicate Location values. Duplicate rows: " + duplicateRows.join(", ")}
        }
        return locationConfig;
    }
}

export const ZancaSortTierConfigCSVParser = {
    FIELDS: ["TierName", "Capacity", "StartLevel", "EndLevel"],
    SAMPLE: 'TierName,Capacity,StartLevel,EndLevel\nBOTTOM,50,1,5\nTOP,60,6,11',
    STRINGIFY: (assets) => {
        let res = "TierName,Capacity,StartLevel,EndLevel\n";
        assets.forEach((asset) => {
            res += `${asset.tierName},${asset.capacity},${asset.startLevel},${asset.endLevel}\n`
        })
        return res;
    },
    PARSE: (str) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, ZancaSortTierConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        // Process rows
        const tierConfig = [];
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate tier config from values
            const values = row.split(',').map(v => v.trim());
            if (values.length !== 4) {
                return {error: `Row ${index + 1} has incorrect number of columns`};
            }
            const config = {
                // The tier name is restricted to a fixed set of values, so a lowercase
                // entry is upcased instead of being rejected
                tierName: values[0].toUpperCase(),
                capacity: parseInt(values[1], 10),
                startLevel: parseInt(values[2], 10),
                endLevel: parseInt(values[3], 10)
            }
            // Validate tier name against the allowed tier names
            if (!ZANCA_SORT_TIER_NAMES.includes(config.tierName)) {
                return {error: `Tier name "${values[0]}" in row ${index + 1} is invalid. `
                    + `Allowed values: ${ZANCA_SORT_TIER_NAMES.join(", ")}`}
            }
            // Validate row
            if (!validateZancaSortTierConfig(config)) {
                return {error: "Values of row number " + (index + 1) + " are invalid"}
            }
            // Check for duplicate tier names
            if (tierConfig.some(t => t.tierName === config.tierName)) {
                return {error: `Duplicate tier name "${config.tierName}" found in row ${index + 1}`}
            }
            // Add to list
            tierConfig.push(config);
        }
        // Check for level overlaps across all tiers
        const overlap = checkTierConfigLevelOverlap(tierConfig);
        if (overlap) {
            return {error: `Level overlap detected between ${overlap.tier1} and ${overlap.tier2}`}
        }
        return tierConfig;
    }
}

export const TesseractStowConfigCSVParser = {
    FIELDS: ["ContainerType", "ContainerLabel"],
    SAMPLE: 'ContainerType,ContainerLabel\nSAMPLE_TYPE_01,A-1\nSAMPLE_TYPE_02,A-2',
    STRINGIFY: (assets) => {
        let res = "ContainerType,ContainerLabel\n";
        assets.forEach((asset) => {
            res += `${asset.containerType},${asset.containerLabel}\n`
        })
        return res;
    },
    PARSE: (str, mac) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, TesseractStowConfigCSVParser);

        // Process rows
        const stowConfig = [];
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate config from values
            const values = row.replace(/\s/g,'').split(',');
            const config  = {
                containerType: values[0],
                containerLabel: values[1]
            }

            // Validate row
            if(!checkModalValidationsForConfig(config, mac, TESSERACT_ASSET_TYPE)) {
                return {error: "Values of row number " + (index+1) + " are invalid"}
            }

            // Add to list
            stowConfig.push(config);
        }
        return stowConfig;
    }
}

export const TesseractTestConfigCSVParser = {
    FIELDS: ["TrackingId","Scannables", "Destination"],
    SAMPLE: 'TrackingId,Scannables,Destination\nTBA123456789,SAMP_TEST_001;TBA123456789,A-1\nTBA123456790,TBA123456790,A-2',
    STRINGIFY: (assets) => {
        let res = "TrackingId,Scannables,Destination\n";
        assets.forEach((asset) => {
            let scannablesString = "";
            asset.scannables.forEach((scannable, index) => {
                if(index) {
                    scannablesString += `;${scannable}`
                } else {
                    scannablesString += scannable;
                }
            });
            res += `${asset.trackingId},${scannablesString},${asset.destination}\n`
        })
        return res;
    },
    PARSE: (str) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, TesseractTestConfigCSVParser);

        // Process rows
        const testConfig = [];
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate test config from values
            const values = row.replace(/\s/g,'').split(',');
            const config = {
                trackingId: values[0],
                scannables: values[1].split(';'),
                destination: values[2]
            }
            // Validate row
            if(!checkTesseractAddTestPackageModalValidations(config)) {
                return {error: "Values of row number " + (index+1) + " are invalid"}
            }
            // Add to list
            testConfig.push(config);
        }
        return testConfig;
    }
}

export const AutoLoaderConfigCSVParser = {
    FIELDS: ["Destination", "InstallationType", "Locations"],
    SAMPLE: 'Destination,InstallationType,Locations\nConveyor_1,aisle,A-1;A-2\nConveyor_2,sideline,B-1;B-2',
    STRINGIFY: (assets) => {
        let res = "Destination,InstallationType,Locations\n";
        assets.forEach((asset) => {
            if ([INSTALLATION_TYPES.SIDELINE.value].includes(asset.installation.type)) {
                res += `${asset.destination},${asset.installation.type},\n`
            } else {
                let locationString = "";
                asset.installation.locations.forEach((location, index) => {
                    if (index) {
                        locationString += `;${location}`;
                    } else {
                        locationString += location;
                    }
                })
                res += `${asset.destination},${asset.installation.type},"${locationString}"\n`
            }
        })
        return res;
    },
    PARSE: (str, mac) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, AutoLoaderConfigCSVParser);
        if (rows.error) {
            return rows;
        }

        // Process each row
        const autoLoaderConfig = [];
        const invalidInstallationTypeIndex = [];
        let hasSideLine = false;
        const validInstallationType = getInstallationTypeValues(AUTO_LOADER_ASSET_TYPE);
        for (let index = 0; index < rows.length; index++) {
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate autoLoader config from values
            const values = row.split(',');
            const config = {
                destination: values[0].replace(/\s/g, ''),
                installation: {
                    type: values[1].toLowerCase().replace(/\s/g, '')
                }
            }

            // add index of row to list if installationType is invalid
            !validInstallationType.includes(config.installation.type) && invalidInstallationTypeIndex.push(index + 1);
            // set has SideLine to true if config contains a sideLine value
            if (INSTALLATION_TYPES.SIDELINE.value === config.installation.type) {
                hasSideLine = true;
            } else {
                config.installation.locations = values.slice(2).join(',').toUpperCase().replace(/\s|"/g, '').split(';');
            }
            // Validate row
            const validateConfig = JSON.parse(JSON.stringify(config))
            if (validateConfig.installation.locations) {
                validateConfig.installation.locations = validateConfig.installation.locations.join(";")
            }
            if (!checkModalValidationsForConfig(validateConfig, mac, AUTO_LOADER_ASSET_TYPE)) {
                return {error: "Values of row number " + (index + 1) + " are invalid"}
            }
            // Add to list
            autoLoaderConfig.push(config);
        }
        if (!hasSideLine) {
            return {error : "Config is missing sideline destination"}
        }
        return invalidInstallationTypeIndex.length > 0
            ? {
                error: "Installation type is invalid for " + invalidInstallationTypeIndex.length + " row(s): "
                    + invalidInstallationTypeIndex.slice(0, Math.min(20, invalidInstallationTypeIndex.length))
            }
            : autoLoaderConfig;
    }
}


export const AutoLoaderTestConfigCSVParser = {
    FIELDS: ["Scannables", "SortZone", "Length", "Height", "Width", "Weight"],
    SAMPLE: 'Scannables,SortZone,Length,Height,Width,Weight\nTBA123456789,A1-1A,100,50,75,5\nTBA123456790,B1-1B,120,60,80,6',
    STRINGIFY: (assets) => {
        let res = "Scannables,SortZone,Length,Height,Width,Weight\n";
        assets.forEach((asset) => {
            let scannablesString = "";
            asset.scannables.forEach((scannable, index) => {
                if (index) {
                    scannablesString += `;${scannable}`
                } else {
                    scannablesString += scannable;
                }
            });
            res += `${scannablesString},${asset.sortZone},${asset.length},${asset.height},${asset.width},${asset.weight}\n`
        })
        return res;
    },
    PARSE: (str) => {
        // Get rows after common validation
        const rows = commonCSVValidation(str, AutoLoaderTestConfigCSVParser);

        // Process rows
        const autoLoaderConfig = [];
        for (let index = 0; index < rows.length; index++) { // Start from 0 instead of 1
            // Handle empty rows
            const row = rows[index];
            if (!row) {
                continue;
            }
            // Generate autoloader config from values
            const values = row.replace(/\s/g, '').split(',');
            if (values.length !== 6) {
                return {error: `Row ${index + 1} has incorrect number of columns`};
            }
            const config = {
                scannables: values[0].split(';'),
                sortZone: values[1],
                length: parseFloat(values[2]),
                height: parseFloat(values[3]),
                width: parseFloat(values[4]),
                weight: parseFloat(values[5])
            }
            // Validate row
            if (!checkModalValidationsForAutoloaderTestPackage(config)) {
                return {error: `Values of row number ${index + 1} are invalid`}
            }
            // Add to list
            autoLoaderConfig.push(config);
        }
        return autoLoaderConfig;
    }
}

export const DivertConfigCSVParser = {
    FIELDS: ["DivertCode", "Clusters"],
    SAMPLE: 'DivertCode,Clusters\n10,A;B',
    STRINGIFY: (assets) => {
        let res = "DivertCode,Clusters\n";
        assets.forEach((asset) => {
            let clusterString = "";
            asset.clusters.forEach((cluster, index) => {
                if(index) {
                    clusterString += `;${cluster}`;
                } else {
                    clusterString += cluster;
                }
            })
            res += `${asset.divertCode},${clusterString}\n`
        })
        return res;
    }
}

export const MHELogsCSVParser = {
    FIELDS: (intl) => {
        return `${getLocalizedMessage({intl, stringId: 'mhelog.datetime'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.protocol'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.devicetype'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.devicename'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.mac'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.ip'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.eventtype'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.message'})},` +
            `${getLocalizedMessage({intl, stringId: 'mhelog.payload'})}\n`
    },
    STRINGIFY: (intl, data, deviceType) => {
        let response = MHELogsCSVParser.FIELDS(intl);
        data.forEach((entry) => {
            response += MHELogsCSVParser.FILTER_DATA(entry.timestamp.replace(COMMA, EMPTY_STRING)) + `,` +
                MHELogsCSVParser.FILTER_DATA(entry.component) + `,` +
                MHELogsCSVParser.FILTER_DATA(deviceType) + `,` +
                MHELogsCSVParser.FILTER_DATA(entry.name) + `,` +
                MHELogsCSVParser.FILTER_DATA(entry.mac) + `,` +
                MHELogsCSVParser.FILTER_DATA(entry.ip) + `,` +
                MHELogsCSVParser.FILTER_DATA(entry.eventType) + `,` +
                MHELogsCSVParser.FILTER_DATA(entry.message) + `,` +
                MHELogsCSVParser.FILTER_DATA(escapeDoubleQuotes(JSON.stringify(entry.payload))) + `\n`;
        })
        return response;
    },
    FILTER_DATA: (data) => {
        return data !== undefined ? data : '';
    }
}

const LOCATION_MAPPING_FIELDS = ["PickChute", "Type", "PickPoints"];
const LOCATION_MAPPING_SAMPLE = "PickChute,Type,PickPoints\nPC1,BAG,PKP-01;PKP-03\nPC2,PACKAGE,PKP-01;PKP-03\n";

export const RetrieverLocationMappingCSVParser = {
    FIELDS: LOCATION_MAPPING_FIELDS,
    SAMPLE: LOCATION_MAPPING_SAMPLE,
    STRINGIFY: (data) => {
        let response = "PickChute,Type,PickPoints\n";
        data.forEach((row) => {
            const locations = Array.isArray(row.locations) ? row.locations.join(";") : "";
            response += `${row.destination},${row.type},${locations}\n`;
        });
        return response;
    }
};

export function createRetrieverLocationMappingCSVParser(pickPointLabels, intl) {
    return {
    FIELDS: LOCATION_MAPPING_FIELDS,
    SAMPLE: LOCATION_MAPPING_SAMPLE,
    STRINGIFY: RetrieverLocationMappingCSVParser.STRINGIFY,
    PARSE: function(text) {
        var fieldParser = {FIELDS: LOCATION_MAPPING_FIELDS};
        var rows = commonCSVValidation(text, fieldParser);
        if (rows.error) return rows;

        var rowError = function(rowNum, message) {
            return {error: intl.formatMessage({id: "locationmapping.error.row_prefix"}, {row: rowNum, message: message})};
        };

        var result = [];
        for (var i = 0; i < rows.length; i++) {
            var row = rows[i].trim();
            if (!row) continue;
            var cols = row.split(",");
            if (cols.length !== 3) {
                return rowError(i + 1, intl.formatMessage({id: "locationmapping.error.column_count"}, {count: cols.length}));
            }
            var destination = cols[0].trim();
            var type = cols[1].trim().toUpperCase();
            var rawLocations = cols[2].trim().split(";").map(function(l) { return l.trim(); }).filter(function(l) { return l; });
            var locations = rawLocations.filter(function(val, idx, arr) { return arr.indexOf(val) === idx; });

            if (pickPointLabels && pickPointLabels.length > 0) {
                for (var j = 0; j < locations.length; j++) {
                    if (pickPointLabels.indexOf(locations[j]) === -1) {
                        return rowError(i + 1, intl.formatMessage({id: "locationmapping.error.pick_point_not_found"}, {pickPoint: locations[j]}));
                    }
                }
            }

            var validationError = validateLocationMappingRow(destination, type, locations, result, false);
            if (validationError) {
                return rowError(i + 1, intl.formatMessage({id: validationError.id}, validationError.values || {}));
            }

            result.push({destination: destination, type: type, locations: locations});
        }
        return result;
    }
    };
}


// WEBPACK FOOTER //
// ./src/components/util/Parsers.js